import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { brandColors } from '../../designTokens';
import { isSignupEnabled } from '../../config/features';
import BrandLogo from '../../components/BrandLogo/BrandLogo';
import { PasswordTextField } from '../../components/PasswordTextField';

interface LoginFormData {
  identifier: string;
  password: string;
  rememberMe: boolean;
}

export const Login: React.FC = () => {
  const { t } = useTranslation('auth');
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    setError: setFieldError,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      identifier: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError(null);
      setIsLoading(true);
      await login(data.identifier, data.password);
    } catch (err: unknown) {
      console.error('Login error:', err);
      const axiosErr = err as { response?: { data?: { message?: string; details?: Array<{ field: string; message: string }> } } };
      // Check if there are field-level validation errors
      const details = axiosErr.response?.data?.details;
      if (details && Array.isArray(details)) {
        // Map backend field names to frontend field names
        details.forEach((detail: { field: string; message: string }) => {
          const fieldMap: Record<string, 'identifier' | 'password'> = {
            'identifier': 'identifier',
            'password': 'password',
          };
          const fieldName = fieldMap[detail.field as string];
          if (fieldName) {
            setFieldError(fieldName, {
              type: 'manual',
              message: detail.message,
            });
          }
        });
        // Show generic validation error message
        setError(axiosErr.response?.data?.message || t('errors.invalidCredentials'));
      } else {
        // Show the error message from the backend or fallback
        setError(axiosErr.response?.data?.message || t('errors.invalidCredentials'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="main"
      sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}
    >
      {/* Brand panel — basalt surface with the negative logo and the brand voice */}
      <Box
        component="aside"
        data-testid="login-brand-panel"
        sx={{
          flex: { sm: '1 1 40%', md: '1 1 45%' },
          backgroundColor: brandColors.basalto,
          color: brandColors.areia,
          p: { xs: 3, sm: 4, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: { xs: 3, md: 6 },
        }}
      >
        <Link
          component={RouterLink}
          to="/"
          aria-label={t('login.backToHome')}
          sx={{ alignSelf: 'flex-start', display: 'inline-flex', borderRadius: 1 }}
        >
          <BrandLogo variant="negative" height={44} />
        </Link>
        <Box>
          <Typography
            variant="citacao"
            component="blockquote"
            sx={{ m: 0, mb: { xs: 1, sm: 2, md: 3 }, fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' }, lineHeight: 1.3 }}
          >
            {t('login.brandQuote')}
          </Typography>
          <Typography variant="body2" sx={{ color: brandColors.pedra }}>
            {t('login.brandTagline')}
          </Typography>
        </Box>
      </Box>

      {/* Form */}
      <Box
        sx={{
          flex: { sm: '1 1 60%', md: '1 1 55%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Typography variant="h1" component="h1" gutterBottom>
            {t('login.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('login.subtitle')}
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ width: '100%' }}
          >
            {/* Identifier Field (email or username) */}
            <Controller
              name="identifier"
              control={control}
              rules={{
                required: t('errors.required'),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('fields.identifier')}
                  fullWidth
                  margin="normal"
                  autoComplete="username"
                  autoFocus
                  error={!!errors.identifier}
                  helperText={errors.identifier?.message}
                  disabled={isLoading}
                />
              )}
            />

            {/* Password Field */}
            <Controller
              name="password"
              control={control}
              rules={{
                required: t('errors.required'),
              }}
              render={({ field }) => (
                <PasswordTextField
                  {...field}
                  label={t('fields.password')}
                  fullWidth
                  margin="normal"
                  autoComplete="current-password"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  disabled={isLoading}
                />
              )}
            />

            {/* Remember Me & Forgot Password */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1,
                mb: 2,
              }}
            >
              <Controller
                name="rememberMe"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        disabled={isLoading}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {t('login.rememberMe')}
                      </Typography>
                    }
                  />
                )}
              />
              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
                sx={{ textDecoration: 'none' }}
              >
                {t('login.forgotPassword')}
              </Link>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                mt: 2,
                mb: 2,
                py: 1.5,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  boxShadow: 'none',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('login.button')
              )}
            </Button>

            {/* Sign Up Link — gated by VITE_FEATURE_SIGNUP */}
            {isSignupEnabled && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('login.noAccount')}{' '}
                  <Link
                    component={RouterLink}
                    to="/signup"
                    sx={{
                      textDecoration: 'none',
                      fontWeight: 600,
                      color: 'primary.main',
                    }}
                  >
                    {t('login.register')}
                  </Link>
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
